import React, { useEffect, useState } from "react";
import axios from "axios";
import { REACT_APP_API_URL } from "../../../../../env";
import CircularProgressBar from "../../../../../Common/ProgressBar/CircularProgressBar";
import {
  validateText,
  validateDate,
  validateDocumentFile,
  sanitizeString,
} from "../../../../../utils/inputValidator";

const NoticeAdminDashboard = () => {
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [generalError, setGeneralError] = useState(null);

  const [form, setForm] = useState({
    NoticeID: 0,
    NoticeDate: "",
    LetterNo: "",
    Headline: "",
    HeadlineUrl: "",
    IsActive: true,
  });

  const [file, setFile] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  const validateForm = () => {
    const errors = {};

    // Validate Notice Date
    const dateValidation = validateDate(form.NoticeDate);
    if (!dateValidation.isValid) errors.NoticeDate = dateValidation.error;

    // Validate Letter No
    if (form.LetterNo) {
      const letterValidation = validateText(form.LetterNo, {
        minLength: 1,
        maxLength: 100,
        rejectHTML: true,
      });
      if (!letterValidation.isValid) errors.LetterNo = letterValidation.error;
    }

    // Validate Headline
    const headlineValidation = validateText(form.Headline, {
      minLength: 3,
      maxLength: 500,
      rejectHTML: true,
    });
    if (!headlineValidation.isValid) errors.Headline = headlineValidation.error;

    // Validate File - Required for new notices, optional for editing
    if (form.NoticeID === 0 || form.NoticeID === '0') {
      // Adding a new notice - PDF is required
      if (!file) {
        errors.file = 'PDF file is required when creating a new notice';
      } else {
        // Check if file is PDF
        if (file.type !== 'application/pdf') {
          errors.file = 'Only PDF files are allowed';
        } else if (file.size > 20 * 1024 * 1024) {
          errors.file = 'File size cannot exceed 20MB';
        }
      }
    } else {
      // Editing existing notice - file is optional but if provided, must be valid
      if (file) {
        if (file.type !== 'application/pdf') {
          errors.file = 'Only PDF files are allowed';
        } else if (file.size > 20 * 1024 * 1024) {
          errors.file = 'File size cannot exceed 20MB';
        }
      }
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  useEffect(() => {
    fetchNotices();
  }, []);

  async function fetchNotices() {
    setLoading(true);
    try {
      const res = await axios.get(
        `${REACT_APP_API_URL}api/notice/all-notice`,{ withCredentials: true }
      );
      setNotices(res.data || []);
    } catch (err) {
      console.error("Fetch error:", err);
      setNotices([]);
    } finally {
      setLoading(false);
    }
  }

  function openAdd() {
    setForm({
      NoticeID: 0,
      NoticeDate: new Date().toISOString().slice(0, 10),
      LetterNo: "",
      Headline: "",
      HeadlineUrl: "",
      IsActive: true,
    });
    setFile(null);
    setFieldErrors({});
    setGeneralError(null);
    setUploadProgress(0);
    setIsUploading(false);
    setShowModal(true);
  }

  function openEdit(n) {
    setForm({
      NoticeID: n.NoticeID,
      NoticeDate: n.NoticeDate ? n.NoticeDate.split("T")[0] : "",
      LetterNo: n.LetterNo || "",
      Headline: n.Headline || "",
      HeadlineUrl: n.HeadlineUrl || "",
      IsActive: !!n.IsActive,
    });
    setFile(null);
    setFieldErrors({});
    setGeneralError(null);
    setUploadProgress(0);
    setIsUploading(false);
    setShowModal(true);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setGeneralError(null);

    if (!validateForm()) {
      setGeneralError("Please fix the errors below");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("NoticeID", form.NoticeID || 0);
      formData.append("NoticeDate", form.NoticeDate);
      formData.append("LetterNo", sanitizeString(form.LetterNo));
      formData.append("Headline", sanitizeString(form.Headline));
      formData.append("HeadlineUrl", sanitizeString(form.HeadlineUrl));
      formData.append("IsActive", form.IsActive ? "true" : "false");
      if (file) formData.append("file", file);

      setIsUploading(true);
      setUploadProgress(0);

      const isEdit = Number(form.NoticeID) > 0;

      await axios({
        method: isEdit ? "put" : "post",
        url: isEdit
          ? `${REACT_APP_API_URL}api/notice/update`
          : `${REACT_APP_API_URL}api/notice/upload`,
        data: formData,
         withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (!e.total) return;
          const percent = Math.round((e.loaded * 100) / e.total);
          setUploadProgress(percent);
        },
      });

      setIsUploading(false);
      setShowModal(false);
      setFieldErrors({});
      fetchNotices();
    } catch (err) {
      console.error("Save error:", err);
      setGeneralError("Error saving notice");
      setIsUploading(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm("Delete this notice?")) return;
    try {
      await axios.delete(
        `${REACT_APP_API_URL}api/notice/delete/${id}`,
         {withCredentials: true}
      );
      fetchNotices();
    } catch (err) {
      console.error("Delete error:", err);
      alert("Delete failed");
    }
  }

  const getFileUrl = (filePath) => {
    if (!filePath) return null;
    return `${REACT_APP_API_URL.replace(/\/$/, "")}${filePath}`;
  };

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3>Notice Dashboard</h3>
        <button className="btn btn-primary" onClick={openAdd}>
          Add Notice
        </button>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <div className="table-responsive">
          <table className="table table-bordered align-middle">
            <thead className="table-light">
              <tr>
                <th>Date</th>
                <th>Letter No</th>
                <th>Headline</th>
                <th>PDF</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {notices.length === 0 && (
                <tr>
                  <td colSpan="5" className="text-center">
                    No notices found
                  </td>
                </tr>
              )}

              {notices.map((n) => (
                <tr key={n.NoticeID}>
                  <td>{n.NoticeDate?.slice(0, 10)}</td>
                  <td>{n.LetterNo}</td>
                  <td>{n.Headline}</td>
                  <td>
                    {n.Files && n.Files.length > 0 ? (
                      <a
                        href={getFileUrl(n.Files[0].FilePath)}
                        target="_blank"
                        rel="noreferrer"
                        className="btn btn-sm btn-outline-primary"
                      >
                        View
                      </a>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td>
                    <button
                      className="btn btn-sm btn-info me-2"
                      onClick={() => openEdit(n)}
                    >
                      Edit
                    </button>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDelete(n.NoticeID)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ================= MODAL ================= */}
      {showModal && (
        <div
          className="modal fade show"
          style={{ display: "block", background: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content" style={{ position: "relative" }}>
              <div className="modal-header">
                <h5 className="modal-title">
                  {form.NoticeID ? "Edit Notice" : "Add Notice"}
                </h5>
                <button
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                />
              </div>

              <div className="modal-body">
                {generalError && (
                  <div className="alert alert-danger alert-dismissible fade show" role="alert">
                    {generalError}
                    <button type="button" className="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
                  </div>
                )}
                <form onSubmit={handleSubmit}>
                  <div className="row">
                    <div className="col-md-3 mb-2">
                      <label className="form-label">Notice Date</label>
                      <input
                        type="date"
                        className={`form-control ${fieldErrors.NoticeDate ? 'is-invalid' : ''}`}
                        value={form.NoticeDate}
                        onChange={(e) =>
                          setForm({ ...form, NoticeDate: e.target.value })
                        }
                        required
                      />
                      {fieldErrors.NoticeDate && <small className="text-danger">{fieldErrors.NoticeDate}</small>}
                    </div>

                    <div className="col-md-3 mb-2">
                      <label className="form-label">Letter No</label>
                      <input
                        className={`form-control ${fieldErrors.LetterNo ? 'is-invalid' : ''}`}
                        value={form.LetterNo}
                        onChange={(e) =>
                          setForm({ ...form, LetterNo: e.target.value })
                        }
                      />
                      {fieldErrors.LetterNo && <small className="text-danger">{fieldErrors.LetterNo}</small>}
                    </div>

                    <div className="col-md-6 mb-2">
                      <label className="form-label">Headline</label>
                      <input
                        className={`form-control ${fieldErrors.Headline ? 'is-invalid' : ''}`}
                        value={form.Headline}
                        onChange={(e) =>
                          setForm({ ...form, Headline: e.target.value })
                        }
                        required
                      />
                      {fieldErrors.Headline && <small className="text-danger">{fieldErrors.Headline}</small>}
                    </div>

                    <div className="col-md-6 mb-2">
                      <label className="form-label">Upload PDF</label>
                      <input
                        type="file"
                        className={`form-control ${fieldErrors.file ? 'is-invalid' : ''}`}
                        accept="application/pdf"
                        onChange={(e) => {
                          const selectedFile = e.target.files[0];
                          if (selectedFile) {
                            // Validate PDF format and size
                            if (selectedFile.type !== 'application/pdf') {
                              alert('Only PDF files are allowed');
                              e.target.value = "";
                              return;
                            }
                            if (selectedFile.size > 20 * 1024 * 1024) {
                              alert('File size cannot exceed 20MB');
                              e.target.value = "";
                              return;
                            }
                          }
                          setFile(selectedFile);
                        }}
                      />
                      {fieldErrors.file && <small className="text-danger">{fieldErrors.file}</small>}
                      {form.NoticeID > 0 && (
                        <small className="text-muted">
                          Upload only to replace existing PDF
                        </small>
                      )}
                      {form.NoticeID === 0 && (
                        <small className="text-muted d-block">
                          PDF is required for new notices
                        </small>
                      )}
                    </div>

                    <div className="col-md-3 mb-2">
                      <label className="form-label">Active</label>
                      <select
                        className="form-control"
                        value={form.IsActive ? "1" : "0"}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            IsActive: e.target.value === "1",
                          })
                        }
                      >
                        <option value="1">Active</option>
                        <option value="0">Inactive</option>
                      </select>
                    </div>
                  </div>

                  <div className="text-end mt-3">
                    <button
                      className="btn btn-success me-2"
                      type="submit"
                      disabled={isUploading}
                    >
                      {isUploading ? "Uploading..." : "Save"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setShowModal(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </form>

                {isUploading && (
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      background: "rgba(255,255,255,0.9)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: 10,
                    }}
                  >
                    <CircularProgressBar progress={uploadProgress} />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NoticeAdminDashboard;